import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/Layout";

const STATUS_OPTIONS = ["ACTIVE", "LOCKED", "COMING_SOON", "HIDDEN"];

export default function Admin() {
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("leads");
  const [intel, setIntel] = useState(null);
  const [bmStaging, setBmStaging] = useState([]);
  const [bmProd, setBmProd] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [checks, setChecks] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editSvc, setEditSvc] = useState(null);
  const [editPlan, setEditPlan] = useState(null);
  const [newSector, setNewSector] = useState(false);
  const [sectorForm, setSectorForm] = useState({ slug: "", name: "", category: "", icon: "Circle", hero_title: "", hero_subtitle: "", description: "", ateco: "" });

  const loadAll = () => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(()=>{});
    api.get("/admin/leads").then(r => setLeads(r.data)).catch(()=>{});
    api.get("/admin/users").then(r => setUsers(r.data)).catch(()=>{});
    api.get("/admin/business-checks").then(r => setChecks(r.data)).catch(()=>{});
    api.get("/sectors").then(r => setSectors(r.data)).catch(()=>{});
    api.get("/services").then(r => setServices(r.data)).catch(()=>{});
    api.get("/plans").then(r => setPlans(r.data)).catch(()=>{});
    api.get("/admin/intelligence").then(r => setIntel(r.data)).catch(()=>{});
    api.get("/admin/benchmarks/staging").then(r => setBmStaging(r.data)).catch(()=>{});
    api.get("/admin/benchmarks/production").then(r => setBmProd(r.data)).catch(()=>{});
  };
  useEffect(loadAll, []);

  const saveService = async () => {
    try {
      await api.patch(`/admin/services/${editSvc.service_id}`, { status: editSvc.status, required_plan: editSvc.required_plan, description: editSvc.description, service_name: editSvc.service_name });
      toast.success("Servizio aggiornato"); setEditSvc(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const savePlan = async () => {
    try {
      await api.patch(`/admin/plans/${editPlan.plan_id}`, { price_monthly: Number(editPlan.price_monthly), price_yearly: Number(editPlan.price_yearly), description: editPlan.description, features: typeof editPlan.features === 'string' ? editPlan.features.split("\n").filter(Boolean) : editPlan.features });
      toast.success("Piano aggiornato"); setEditPlan(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveSector = async () => {
    try {
      await api.post("/admin/sectors", sectorForm);
      toast.success("Settore creato"); setNewSector(false); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const TABS = [["leads","Waitlist"],["users","Utenti"],["checks","Business Check"],["sectors","Settori"],["services","Servizi"],["plans","Piani & Prezzi"],["benchmarks","Benchmark QA"],["intelligence","Customer Intelligence"]];

  const approveBm = async (sid) => {
    try { const r = await api.post("/admin/benchmarks/approve", { staging_id: sid }); toast.success(`Approvato — confidence ${r.data.confidence}, produzione ${r.data.production_enabled ? "ON" : "OFF"}`); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rejectBm = async (sid) => {
    try { await api.post("/admin/benchmarks/reject", { staging_id: sid, reason: "admin" }); toast.success("Rifiutato"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleBm = async (lid, enabled) => {
    try { await api.post("/admin/benchmarks/toggle", { lineage_id: lid, production_enabled: enabled }); toast.success(enabled ? "Attivato" : "Disattivato"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-extrabold tracking-tight">Pannello Admin</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {[["Utenti",stats.users],["Lead waitlist",stats.leads],["Business Check",stats.checks],["Pagamenti",stats.payments]].map(([l,v],i) => (
          <div key={i} className="ic-card p-5" data-testid={`admin-stat-${i}`}>
            <p className="text-xs text-[#7A7788] uppercase tracking-widest">{l}</p>
            <p className="text-3xl font-bold text-[#5424D5]">{v ?? 0}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 flex gap-1 border-b border-[#E5E4EB] overflow-x-auto">
        {TABS.map(([id,l]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap ${tab === id ? 'text-[#5424D5] border-b-2 border-[#5424D5]' : 'text-[#524F5E]'}`}
            data-testid={`admin-tab-${id}`}>{l}</button>
        ))}
      </div>

      {/* LEADS / USERS / CHECKS */}
      {(tab === "leads" || tab === "users" || tab === "checks") && (
        <div className="mt-6 ic-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F7]"><tr>
              {tab === "leads" && <><th className="text-left p-3">Email</th><th className="text-left p-3">Servizio</th><th className="text-left p-3">Data</th></>}
              {tab === "users" && <><th className="text-left p-3">Email</th><th className="text-left p-3">Nome</th><th className="text-left p-3">Ruolo</th><th className="text-left p-3">Provider</th></>}
              {tab === "checks" && <><th className="text-left p-3">Settore</th><th className="text-left p-3">Score</th><th className="text-left p-3">Data</th></>}
            </tr></thead>
            <tbody>
              {tab === "leads" && leads.map((l,i) => <tr key={i} className="border-t border-[#E5E4EB]"><td className="p-3">{l.email}</td><td className="p-3">{l.service}</td><td className="p-3 text-xs text-[#7A7788]">{l.created_at?.slice(0,10)}</td></tr>)}
              {tab === "users" && users.map((u,i) => <tr key={i} className="border-t border-[#E5E4EB]"><td className="p-3">{u.email}</td><td className="p-3">{u.name}</td><td className="p-3"><span className="ic-badge status-active">{u.role}</span></td><td className="p-3 text-xs">{u.auth_provider}</td></tr>)}
              {tab === "checks" && checks.map((c,i) => <tr key={i} className="border-t border-[#E5E4EB]"><td className="p-3">{c.sector_slug}</td><td className="p-3 font-bold text-[#5424D5]">{c.score}</td><td className="p-3 text-xs text-[#7A7788]">{c.created_at?.slice(0,10)}</td></tr>)}
            </tbody>
          </table>
        </div>
      )}

      {/* SECTORS */}
      {tab === "sectors" && (
        <div className="mt-6">
          <button onClick={() => setNewSector(true)} className="ic-btn-primary text-sm mb-4" data-testid="admin-add-sector">+ Nuovo settore</button>
          {newSector && (
            <div className="ic-card p-6 mb-6 grid md:grid-cols-2 gap-3">
              {[["slug","Slug (es. gelateria)"],["name","Nome"],["category","Categoria"],["icon","Icona Lucide"],["ateco","Codice ATECO"]].map(([k,ph]) => (
                <input key={k} placeholder={ph} value={sectorForm[k]} onChange={e => setSectorForm({...sectorForm,[k]:e.target.value})} className="px-4 py-2 rounded-xl border border-[#E5E4EB]" data-testid={`new-sector-${k}`} />
              ))}
              <input placeholder="Hero title" value={sectorForm.hero_title} onChange={e => setSectorForm({...sectorForm,hero_title:e.target.value})} className="px-4 py-2 rounded-xl border border-[#E5E4EB] md:col-span-2" data-testid="new-sector-hero" />
              <textarea placeholder="Descrizione" value={sectorForm.description} onChange={e => setSectorForm({...sectorForm,description:e.target.value})} className="px-4 py-2 rounded-xl border border-[#E5E4EB] md:col-span-2" rows={2} data-testid="new-sector-desc" />
              <div className="md:col-span-2 flex gap-2">
                <button onClick={saveSector} className="ic-btn-primary text-sm" data-testid="new-sector-save">Salva settore</button>
                <button onClick={() => setNewSector(false)} className="ic-btn-ghost text-sm">Annulla</button>
              </div>
            </div>
          )}
          <div className="ic-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F7]"><tr><th className="text-left p-3">Nome</th><th className="text-left p-3">Slug</th><th className="text-left p-3">Categoria</th><th className="text-left p-3">ATECO</th><th className="text-left p-3">MVP</th></tr></thead>
              <tbody>
                {sectors.map((s,i) => <tr key={i} className="border-t border-[#E5E4EB]" data-testid={`admin-sector-${s.slug}`}><td className="p-3 font-medium">{s.name}</td><td className="p-3 text-xs">{s.slug}</td><td className="p-3 text-xs">{s.category}</td><td className="p-3 text-xs">{s.ateco}</td><td className="p-3">{s.is_mvp ? "✓" : "—"}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SERVICES */}
      {tab === "services" && (
        <div className="mt-6 ic-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F7]"><tr><th className="text-left p-3">Servizio</th><th className="text-left p-3">Stato</th><th className="text-left p-3">Piano richiesto</th><th className="text-left p-3">Azioni</th></tr></thead>
            <tbody>
              {services.map((s,i) => (
                <tr key={i} className="border-t border-[#E5E4EB]" data-testid={`admin-service-${s.service_id}`}>
                  <td className="p-3 font-medium">{s.service_name}</td>
                  <td className="p-3"><StatusBadge status={s.status} /></td>
                  <td className="p-3 text-xs">{s.required_plan}</td>
                  <td className="p-3"><button onClick={() => setEditSvc({...s})} className="text-[#5424D5] font-semibold text-xs" data-testid={`edit-service-${s.service_id}`}>Modifica</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {editSvc && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="service-edit-modal">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3">
                <h3 className="font-bold text-lg">{editSvc.service_name}</h3>
                <label className="block text-sm"><span className="font-semibold">Stato</span>
                  <select value={editSvc.status} onChange={e => setEditSvc({...editSvc, status: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E5E4EB]" data-testid="service-status-select">
                    {STATUS_OPTIONS.map(o => <option key={o}>{o}</option>)}
                  </select>
                </label>
                <label className="block text-sm"><span className="font-semibold">Piano richiesto</span>
                  <select value={editSvc.required_plan} onChange={e => setEditSvc({...editSvc, required_plan: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E5E4EB]" data-testid="service-plan-select">
                    {["free","control","grow","cfo"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </label>
                <label className="block text-sm"><span className="font-semibold">Descrizione</span>
                  <textarea value={editSvc.description} onChange={e => setEditSvc({...editSvc, description: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E5E4EB]" rows={3} data-testid="service-desc-input" />
                </label>
                <div className="flex gap-2 pt-2">
                  <button onClick={saveService} className="ic-btn-primary text-sm" data-testid="service-save">Salva</button>
                  <button onClick={() => setEditSvc(null)} className="ic-btn-ghost text-sm">Annulla</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PLANS */}
      {tab === "plans" && (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(p => (
            <div key={p.plan_id} className="ic-card p-5" data-testid={`admin-plan-${p.plan_id}`}>
              <h4 className="font-bold">{p.name}</h4>
              <p className="text-2xl font-extrabold text-[#5424D5] mt-2">€{p.price_monthly}<span className="text-xs font-normal text-[#7A7788]">/m</span></p>
              <p className="text-sm text-[#7A7788]">€{p.price_yearly}/m annuale</p>
              <button onClick={() => setEditPlan({...p, features: p.features.join("\n")})} className="mt-3 text-sm text-[#5424D5] font-semibold" data-testid={`edit-plan-${p.plan_id}`}>Modifica prezzi</button>
            </div>
          ))}
          {editPlan && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="plan-edit-modal">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
                <h3 className="font-bold text-lg">{editPlan.name}</h3>
                <label className="block text-sm"><span className="font-semibold">Prezzo mensile (€)</span>
                  <input type="number" value={editPlan.price_monthly} onChange={e => setEditPlan({...editPlan, price_monthly: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E5E4EB]" data-testid="plan-price-monthly" />
                </label>
                <label className="block text-sm"><span className="font-semibold">Prezzo annuale (€/mese)</span>
                  <input type="number" value={editPlan.price_yearly} onChange={e => setEditPlan({...editPlan, price_yearly: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E5E4EB]" data-testid="plan-price-yearly" />
                </label>
                <label className="block text-sm"><span className="font-semibold">Descrizione</span>
                  <textarea value={editPlan.description} onChange={e => setEditPlan({...editPlan, description: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E5E4EB]" rows={2} data-testid="plan-desc" />
                </label>
                <label className="block text-sm"><span className="font-semibold">Features (una per riga)</span>
                  <textarea value={editPlan.features} onChange={e => setEditPlan({...editPlan, features: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E5E4EB]" rows={5} data-testid="plan-features" />
                </label>
                <div className="flex gap-2 pt-2">
                  <button onClick={() => savePlan()} className="ic-btn-primary text-sm" data-testid="plan-save">Salva</button>
                  <button onClick={() => setEditPlan(null)} className="ic-btn-ghost text-sm">Annulla</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* BENCHMARK QA — solo admin */}
      {tab === "benchmarks" && (
        <div className="mt-6 space-y-8" data-testid="admin-benchmarks">
          <div>
            <h3 className="font-bold text-lg mb-1">Import in staging</h3>
            <p className="text-xs text-[#7A7788] mb-3">Nessun dato entra in produzione senza validazione + approvazione. Record rifiutati non sono attivabili.</p>
            <div className="ic-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F7]"><tr>
                  <th className="text-left p-3">KPI</th><th className="text-left p-3">Fonte</th><th className="text-left p-3">Anno</th>
                  <th className="text-left p-3">Classificazione</th><th className="text-left p-3">Stato QA</th><th className="text-left p-3">Errori</th><th className="text-left p-3">Azioni</th>
                </tr></thead>
                <tbody>
                  {bmStaging.map((s) => (
                    <tr key={s.staging_id} className="border-t border-[#E5E4EB]" data-testid={`bm-staging-${s.staging_id}`}>
                      <td className="p-3 font-medium">{s.data?.kpi_code}</td>
                      <td className="p-3 text-xs">{s.data?.source_id}</td>
                      <td className="p-3 text-xs">{s.data?.reference_year}</td>
                      <td className="p-3 text-xs">{s.data?.classification_type} {s.data?.classification_code}</td>
                      <td className="p-3"><span className={`ic-badge ${s.qa_status === "VALIDATED" ? "status-active" : s.qa_status === "APPROVED" ? "status-active" : "status-coming"}`}>{s.qa_status}</span></td>
                      <td className="p-3 text-xs text-[#C2410C] max-w-[220px]">{s.validation?.errors?.join(", ") || s.validation?.warnings?.join(", ") || "—"}</td>
                      <td className="p-3 text-xs whitespace-nowrap">
                        {s.qa_status === "VALIDATED" && (
                          <>
                            <button onClick={() => approveBm(s.staging_id)} className="text-[#047857] font-semibold mr-2" data-testid={`bm-approve-${s.staging_id}`}>Approva</button>
                            <button onClick={() => rejectBm(s.staging_id)} className="text-[#C2410C] font-semibold" data-testid={`bm-reject-${s.staging_id}`}>Rifiuta</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bmStaging.length === 0 && <p className="p-6 text-center text-sm text-[#7A7788]">Nessun import in staging.</p>}
            </div>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-1">Benchmark attivi (produzione)</h3>
            <p className="text-xs text-[#7A7788] mb-3">Solo record con lineage completo e confidence ≥ 70 sono production-enabled di default.</p>
            <div className="ic-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F7]"><tr>
                  <th className="text-left p-3">KPI</th><th className="text-left p-3">Fonte</th><th className="text-left p-3">Anno</th>
                  <th className="text-right p-3">Mediana</th><th className="text-left p-3">Confidence</th><th className="text-left p-3">Produzione</th><th className="text-left p-3">Lineage</th>
                </tr></thead>
                <tbody>
                  {bmProd.map((b, i) => (
                    <tr key={i} className="border-t border-[#E5E4EB]" data-testid={`bm-prod-${i}`}>
                      <td className="p-3 font-medium">{b.kpi_code}</td>
                      <td className="p-3 text-xs">{b.source_id}</td>
                      <td className="p-3 text-xs">{b.reference_year}</td>
                      <td className="p-3 text-right">{b.median_normalized ?? b.median ?? "—"}</td>
                      <td className="p-3"><span className="ic-badge status-active">{b.confidence?.grade} ({b.confidence_score})</span></td>
                      <td className="p-3">
                        <button onClick={() => toggleBm(b.benchmark_lineage_id, !b.production_enabled)}
                          className={`text-xs font-semibold ${b.production_enabled ? "text-[#047857]" : "text-[#7A7788]"}`}
                          data-testid={`bm-toggle-${i}`}>{b.production_enabled ? "ON — disattiva" : "OFF — attiva"}</button>
                      </td>
                      <td className="p-3 text-[10px] text-[#7A7788]">{b.benchmark_lineage_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bmProd.length === 0 && <p className="p-6 text-center text-sm text-[#7A7788]">Nessun benchmark in produzione. Normale finché non carichi dati reali QA-approvati.</p>}
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMER INTELLIGENCE — solo dati aggregati, mai P.IVA/CF */}
      {tab === "intelligence" && (
        <div className="mt-6" data-testid="admin-intelligence">
          <p className="text-xs text-[#7A7788] mb-4">Tutti i dati sono aggregati. P.IVA e Codice Fiscale non sono mai esposti in questa vista.</p>
          {intel ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[["Utenti", intel.users], ["Aziende registrate", intel.companies], ["Business Check", intel.checks], ["Pagamenti", intel.paid]].map(([l, v], i) => (
                  <div key={i} className="ic-card p-5"><p className="text-xs text-[#7A7788] uppercase tracking-widest">{l}</p><p className="text-3xl font-bold text-[#5424D5]">{v}</p></div>
                ))}
              </div>
              <h3 className="font-bold mt-8 mb-3">Funnel eventi</h3>
              <div className="grid md:grid-cols-3 gap-3">
                {["homepage_view","business_check_started","business_check_completed","pricing_viewed","checkout_started","service_interest_submitted","company_profile_completed"].map(ev => (
                  <div key={ev} className="ic-card p-4 flex justify-between"><span className="text-sm">{ev}</span><span className="font-bold text-[#FF6A24]">{intel.events?.[ev] || 0}</span></div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-5 mt-8">
                {[["Clienti per settore", intel.by_sector], ["Clienti per regione", intel.by_region], ["Clienti per fascia fatturato", intel.by_revenue_band], ["Clienti per forma giuridica", intel.by_legal_form], ["Aziende per piano", intel.by_plan], ["Clienti per source", intel.by_source]].map(([title, obj], i) => (
                  <div key={i} className="ic-card p-5">
                    <p className="font-semibold text-sm mb-3">{title}</p>
                    {obj && Object.keys(obj).length > 0 ? (
                      <ul className="space-y-1.5 text-sm">
                        {Object.entries(obj).map(([k, v]) => (
                          <li key={k} className="flex justify-between"><span className="text-[#524F5E]">{k}</span><span className="font-bold">{v}</span></li>
                        ))}
                      </ul>
                    ) : <p className="text-xs text-[#7A7788]">Nessun dato ancora.</p>}
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-sm text-[#7A7788]">Caricamento…</p>}
        </div>
      )}
    </div>
  );
}
